import { useEffect, useRef, useState } from 'react'
import { Input } from '@/components/ui/input'
import { apiClient } from '@/services/api.client'

interface EmployeeResult {
	employeeId: string
	displayName: string
	email?: string | null
}

interface EmployeeAutocompleteProps {
	value: string | null | undefined
	displayValue?: string
	onChange: (employeeId: string | null, displayName: string) => void
	placeholder?: string
	className?: string
}

export function EmployeeAutocomplete({
	value,
	displayValue,
	onChange,
	placeholder = 'Search employee...',
	className,
}: EmployeeAutocompleteProps) {
	const [query, setQuery] = useState(displayValue ?? '')
	const [results, setResults] = useState<EmployeeResult[]>([])
	const [isOpen, setIsOpen] = useState(false)
	const [isSearching, setIsSearching] = useState(false)
	const containerRef = useRef<HTMLDivElement>(null)
	const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

	useEffect(() => {
		setQuery(displayValue ?? '')
	}, [displayValue])

	useEffect(() => {
		const handleClickOutside = (e: MouseEvent) => {
			if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
				setIsOpen(false)
			}
		}
		document.addEventListener('mousedown', handleClickOutside)
		return () => document.removeEventListener('mousedown', handleClickOutside)
	}, [])

	const search = (q: string) => {
		setQuery(q)

		if (q.length < 2) {
			setResults([])
			setIsOpen(false)
			if (q === '') onChange(null, '')
			return
		}

		if (debounceRef.current) clearTimeout(debounceRef.current)
		debounceRef.current = setTimeout(() => {
			setIsSearching(true)
			void apiClient
				.get<EmployeeResult[]>(`/employees?filters[displayName]=${encodeURIComponent(q)}&limit=10`)
				.then((res) => {
					const items = res.data || []
					setResults(items)
					setIsOpen(items.length > 0)
				})
				.catch(() => setResults([]))
				.finally(() => setIsSearching(false))
		}, 300)
	}

	const selectResult = (result: EmployeeResult) => {
		setQuery(result.displayName)
		onChange(result.employeeId, result.displayName)
		setIsOpen(false)
		setResults([])
	}

	const handleClear = () => {
		setQuery('')
		onChange(null, '')
		setResults([])
		setIsOpen(false)
	}

	return (
		<div ref={containerRef} className="relative">
			<div className="relative">
				<Input
					value={query}
					onChange={(e) => search(e.target.value)}
					onFocus={() => results.length > 0 && setIsOpen(true)}
					placeholder={placeholder}
					className={className}
				/>
				{value && (
					<button
						type="button"
						onClick={handleClear}
						className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 text-xs"
						aria-label="Clear owner"
					>
						&times;
					</button>
				)}
			</div>
			{isSearching && (
				<div className="absolute right-2 top-1/2 -translate-y-1/2">
					<div className="animate-spin rounded-full h-3 w-3 border border-gray-400 border-t-transparent" />
				</div>
			)}
			{isOpen && results.length > 0 && (
				<div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg max-h-48 overflow-auto">
					{results.map((r) => (
						<button
							key={r.employeeId}
							type="button"
							className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 flex flex-col"
							onMouseDown={(e) => e.preventDefault()}
							onClick={() => selectResult(r)}
						>
							<span className="font-medium text-gray-900">{r.displayName}</span>
							{r.email && <span className="text-xs text-gray-500">{r.email}</span>}
						</button>
					))}
				</div>
			)}
		</div>
	)
}
